import { useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { ExpressRevealContext, type ExpressRevealValue } from '@/context/ExpressRevealContext';
import { BottomSheetContext } from '@/context/BottomSheetContext';
import { PaymentMethodEnum } from '@/types/common';
import { PreferredPaymentSkeletonContent } from '@/components/CheckoutSkeletonLoader/PreferredPaymentSkeleton';
import { PollingStatus } from '@/types/order';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import {
  EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS,
  EXPRESS_WALLET_GATE_HANG_TIMEOUT_REASON,
  useExpressReadyTimeout,
} from '@/hooks/useExpressReadyTimeout';
import { logError } from '@/utils/logger';
import { FeatureFlag } from '@/constants/featureFlags';
import { Modal } from '@/components/Common/Modal';
import { CheckoutBottomSheet } from '@/components/Common/CheckoutBottomSheet';
import { isRedirectModeRequested } from '@/utils/checkoutMode';
import { XButton } from '@/components/XButton/XButton';
import { Details } from '@/components/Details/Details';
import { Footer } from '@/components/Footer/Footer';
import { Card3DSForm } from '@/components/Card3DSForm/Card3DSForm';
import { ProcessingLoader } from '@/components/ProcessingLoader/ProcessingLoader';
import { ScreenDrawer } from '@/components/ScreenDrawer/ScreenDrawer';
import { RedirectPayment } from '@/components/RedirectPayment/RedirectPayment';
import { SuccessScreen } from '@/components/SuccessScreen/SuccessScreen';
import { Divider } from '@/components/Common/Divider';
import { Payment } from '@/components/Payment/Payment';
import { TopWrapper } from '@/components/TopWrapper/TopWrapper';
import { DRAWER_SUPPORTED_PAYMENT_METHODS } from '@/constants/lastPaymentMethod';
import { hasPublisherHaveBackgroundImage } from '@/utils/publisher';
import { UseOtherPaymentMethodLink } from '@/components/UseOtherPaymentMethodLink/UseOtherPaymentMethodLink';
import { useOtherPaymentMethodFallback } from '@/hooks/useOtherPaymentMethodFallback';
import type { TemplateViewProps } from '@/templates/CheckoutTemplate';
import { DefaultView } from './DefaultView';
import { PromoCode } from '@/components/PromoCode/PromoCode';

/**
 * Mobile-only "preferred payment method" view: one primary CTA with a
 * subtle "Use other payment method" escape hatch. Falls back to
 * {@link DefaultView} on non-mobile viewports so a desktop resize after
 * init still renders correctly.
 *
 * Shared by both PREFERRED_PAYMENT_METHOD branches in
 * {@link PreferredPaymentTemplate}:
 *
 *   - FTD (first-time depositor) — primary CTA is the device's native
 *     express wallet (Apple Pay / Google Pay), selected by the template's
 *     `resolveDefaultPaymentMethod`.
 *
 *   - LAST-METHOD (returning depositor) — primary CTA is the BE-supplied
 *     `bootData.checkoutConfiguration.preferredPaymentMethod`. PRD-scoped
 *     to apple/google/paypal/link/card/amazon_pay/blik/blik_checkout/pix (see
 *     `SUPPORTED_LAST_PAYMENT_METHODS`). The same `<Payment />` slot
 *     renders whichever method was selected via `PaymentMethodRenderer`;
 *     no branching needed here.
 *
 * The escape-hatch link is gated by the `secondaryUseOtherPaymentMethodLink`
 * template feature flag and swaps the active template to DEFAULT, picking
 * DEFAULT's default method so the user lands on the full picker with
 * something sensible pre-selected.
 */
export const PreferredPaymentView = ({ statusOrderPolling }: TemplateViewProps) => {
  const context = useContext(CheckoutContext);
  const {
    viewer: { screen },
    redirectPayment: { redirectUrl, showRedirect },
    successScreen,
    featureFlags,
    template,
    paymentMethodSelected,
    paymentMethodsAvailable,
    boot,
    actions: { closeCheckout },
  } = context;
  const isMobile = useIsMobile();
  const landscape = useIsLandscape();
  // FTD / last-payment with Apple Pay must stay PURELY a skeleton until the PSP
  // confirms the wallet — the player should never glimpse the FTD chrome only
  // to have it swap to DEFAULT when Apple Pay turns out unsupported. We mount
  // the real content (incl. the Apple Pay probe) hidden under the skeleton so
  // its SDK boots; the form calls `onWalletReady` once available, or the
  // existing unavailable/timeout path swaps us to DEFAULT.
  const [walletRevealed, setWalletRevealed] = useState(false);
  const gateOnWallet =
    template.features.deferExpressWalletReveal && paymentMethodSelected === PaymentMethodEnum.APPLE_PAY;
  const showWalletGate = gateOnWallet && !walletRevealed;
  // Keyed on the gate alone, so the identity holds across ordinary renders and
  // only moves when the gate actually lifts — once per screen.
  const revealValue = useMemo(
    () => ({ onWalletReady: () => setWalletRevealed(true), isRevealed: !showWalletGate }),
    [showWalletGate]
  );
  // Bottom-sheet (Apple/Google-Pay-style) presentation: mobile portrait + the
  // publisher flag on + the PREFERRED primary method is inside
  // `DRAWER_SUPPORTED_PAYMENT_METHODS` (apple / google / paypal / card / blik /
  // blik_checkout / pix). The method is whatever the template resolved, so this
  // covers BOTH branches — an FTD nominated a local APM for their market and a
  // returning shopper whose last method was one. PREFERRED's own eligibility
  // (`SUPPORTED_LAST_PAYMENT_METHODS`) is still wider: LINK / AMAZON_PAY render
  // inside PREFERRED via the byte-identical legacy `<Modal>` branch below.
  // Landscape, desktop, and flag-off all fall through to that same modal.
  // Never in `?mode=redirect`: the sheet carries its own close X and
  // drag-to-dismiss that call `closeCheckout` directly, bypassing
  // `shouldHideXButton` — in a full-page checkout both would post
  // CLOSE_CHECKOUT to a host that isn't there and strand the shopper.
  // Falling through drops mobile portrait into the modal branch below, the
  // same one landscape and flag-off sessions already get.
  const useDrawer =
    isMobile &&
    DRAWER_SUPPORTED_PAYMENT_METHODS.has(paymentMethodSelected) &&
    Boolean(featureFlags?.[FeatureFlag.CHECKOUT_DRAWER_FOR_LAST_METHOD]) &&
    !isRedirectModeRequested();

  // Backstop of last resort. Every other hang-breaker is armed by the wallet
  // form under the gate, so all of them assume that form mounts at all — this
  // one is owned by the gate, so the skeleton is bounded no matter what renders
  // (or fails to render) beneath it. Both actions are deliberate: report so the
  // template degrades to the full picker (a usable payment path), and reveal so
  // the skeleton lifts even if the active template declines to fall back.
  //
  // Armed ONLY where the skeleton is actually painted — the landscape column
  // and the full-screen modal. Desktop delegates to `DefaultView` and the
  // bottom sheet is ungated by design, so arming there would time out a
  // perfectly healthy render and swap the template out from under it.
  const gateIsPainted = showWalletGate && (landscape || (isMobile && !useDrawer));
  const reportWalletAvailability = useExpressWalletFallback();
  useExpressReadyTimeout(
    gateIsPainted,
    () => {
      logError('Express wallet gate never lifted; forcing fallback', undefined, {
        template: template.type,
        paymentMethod: paymentMethodSelected,
        timeoutMs: EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS,
      });
      reportWalletAvailability(false, {
        wallet: 'apple_pay',
        provider:
          paymentMethodsAvailable.find((pm) => pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY)?.provider ??
          'unknown',
        reason: EXPRESS_WALLET_GATE_HANG_TIMEOUT_REASON,
        timeoutMs: EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS,
      });
      setWalletRevealed(true);
    },
    EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS
  );
  const showPaymentMethodIcons = Boolean(featureFlags?.[FeatureFlag.CHECKOUT_SHOW_PAYMENT_METHOD_ICONS]);
  const showUseOtherPaymentMethodLink = template.features.secondaryUseOtherPaymentMethodLink;

  // Mobile-only template, so prefer the publisher's mobile background asset
  // and fall back to the desktop one — same precedence as TopWrapper.
  const hasCheckoutBackground = hasPublisherHaveBackgroundImage(boot?.checkoutTheme, template.type);
  const checkoutBackgroundImage = hasCheckoutBackground
    ? boot?.checkoutTheme?.mobileBgImage || boot?.checkoutTheme?.backgroundImage
    : undefined;

  const { handleUseOtherPaymentMethod } = useOtherPaymentMethodFallback();

  // True desktop only — width >= 1024 (or any viewport outside the landscape
  // phone band). The landscape branch below handles 700 <= w <= 1023, h <= 500.
  if (!isMobile && !landscape) {
    return <DefaultView statusOrderPolling={statusOrderPolling} />;
  }

  // Hide the escape-hatch link while a viewer sub-screen (select card / add new
  // card) is open: in landscape that sub-screen renders inline inside the same
  // column as the link, so the link would otherwise sit awkwardly below the
  // card form (see screenshot). The sub-screen has its own Cancel action, so
  // the escape hatch is redundant there.
  const useOtherPaymentMethodLink =
    showUseOtherPaymentMethodLink && !screen ? (
      <UseOtherPaymentMethodLink onClick={handleUseOtherPaymentMethod} showIcons={showPaymentMethodIcons} />
    ) : null;

  // Landscape phone branch — reuse the DEFAULT 40/60 split shell (TopWrapper
  // on the left, payment column on the right) but keep the PREFERRED single
  // CTA + escape-hatch link in the right column. The shared template config
  // (`mobileOnlyExpressFirstLayout: true` + landscape-aware
  // `isExpressFirstMobile` in `<Payment />`) collapses the right column down
  // to just the auto-selected wallet form, so the visual result mirrors
  // images 1 & 2 of the design.
  if (landscape) {
    return (
      <Modal>
        <ExpressRevealContext.Provider value={revealValue}>
          {showWalletGate && (
            <div data-testid="preferred-payment-wallet-gate" aria-hidden className="absolute inset-0 z-20 bg-white">
              <PreferredPaymentSkeletonContent />
            </div>
          )}
          <div
            data-testid="preferred-payment-method"
            className="flex h-[100dvh] w-full landscape:flex-row overflow-hidden bg-white"
          >
            <TopWrapper />
            {!landscape && isMobile && <PromoCode />}
            <div className="relative flex flex-col landscape:w-[56%] landscape:h-[100dvh] landscape:overflow-y-auto">
              <XButton onClick={() => closeCheckout(context)} />
              {/*
               * `my-auto` centers the CTA vertically when content is short, but
               * (unlike `flex-1 justify-center`) collapses to 0 when content is
               * tall — so the column overflows and scrolls naturally, carrying
               * the Footer with it instead of pinning it. This also stops the
               * top of tall content (e.g. the inline add-card form) from being
               * clipped, which `justify-center` + overflow would do.
               */}
              <div
                data-testid="preferred-payment-cta"
                className="flex w-full flex-col items-center gap-5 px-4 py-6 my-auto"
              >
                <div className="w-full max-w-90 flex flex-col gap-5">
                  <Payment />
                  {useOtherPaymentMethodLink}
                </div>
              </div>
              <div className="w-full flex-shrink-0 px-4">
                <Footer />
              </div>
            </div>
            <Card3DSForm />
            <ProcessingLoader />
            {(redirectUrl || showRedirect) && <RedirectPayment />}
            {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
          </div>
        </ExpressRevealContext.Provider>
      </Modal>
    );
  }

  if (useDrawer) {
    return (
      <PreferredPaymentDrawerView
        revealValue={revealValue}
        onClose={() => closeCheckout(context)}
        useOtherPaymentMethodLink={useOtherPaymentMethodLink}
        showRedirect={Boolean(redirectUrl || showRedirect)}
        showSuccessScreen={Boolean(successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS)}
      />
    );
  }

  return (
    <Modal>
      <ExpressRevealContext.Provider value={revealValue}>
        {/* Full-screen skeleton overlay: identical markup to the boot skeleton,
            painted on top of the (mounted-but-covered) live view until Apple Pay
            is confirmed. `absolute inset-0` covers the `relative` Modal content. */}
        {showWalletGate && (
          <div data-testid="preferred-payment-wallet-gate" aria-hidden className="absolute inset-0 z-20 bg-white">
            <PreferredPaymentSkeletonContent />
          </div>
        )}
        <div data-testid="preferred-payment-method" className="flex h-full w-full flex-col bg-white overflow-y-auto">
          {/*
           * Mobile layout — adaptive between two viewport regimes:
           *
           *   Tall viewport (iPhone Pro / Pro Max, Android big phones):
           *     content + CTA + Footer fit inside `h-full`. The flex-1
           *     spacer absorbs the leftover space between CTA and Footer
           *     so the Footer stays pinned to the modal's bottom edge.
           *
           *   Short viewport (iPhone SE 375×667 and similar small devices):
           *     content + CTA + Footer don't fit. The spacer collapses
           *     (basis 0, shrink 1) and the WHOLE modal scrolls as one
           *     natural column — there is no inner scroll boundary
           *     squeezing the items list. The user scrolls vertically
           *     once instead of being forced into a 397px-tall window
           *     where Total + Use-other-link are cramped against the CTA.
           *
           *   ┌────────────────────────────────────────┐
           *   │  bg image band (XButton + Details)     │ natural height
           *   │  [Divider — only when NO bg image]     │ ← bg ends here
           *   ├────────────────────────────────────────┤
           *   │  CTA (Payment + Use other link)        │ shrink-0
           *   ├────────────────────────────────────────┤
           *   │  Spacer (grows on tall, collapses on   │ flex-1, basis 0
           *   │  short)                                │
           *   ├────────────────────────────────────────┤
           *   │  Footer                                │ shrink-0
           *   └────────────────────────────────────────┘
           *
           * `overflow-y-auto` lives on this OUTER column, NOT on an inner
           * row. That's what unlocks the "single natural scroll on small
           * devices" behavior — children don't bound themselves with
           * `min-h-0`, so on tight viewports they overflow the column
           * cleanly and the column scrolls.
           */}
          <div
            data-testid="preferred-payment-background"
            className="flex w-full flex-shrink-0 flex-col pt-3 bg-cover bg-top"
            style={checkoutBackgroundImage ? { backgroundImage: `url(${checkoutBackgroundImage})` } : undefined}
          >
            <XButton onClick={() => closeCheckout(context)} />
            <div className="mt-2 px-4">
              <Details />
            </div>
          </div>
          {/* Divider only when the publisher has no background image —
            with a background, the image edge is already the visual break
            between Details and the CTA, so a separate hairline reads as
            noise. */}
          {!hasCheckoutBackground && (
            <div className="w-full flex-shrink-0">
              <Divider />
            </div>
          )}
          <PromoCode />
          <div data-testid="preferred-payment-cta" className="flex w-full flex-shrink-0 flex-col gap-5 px-4 pb-5 pt-6">
            <Payment />
            {useOtherPaymentMethodLink}
          </div>
          <div data-testid="preferred-payment-bottom-spacer" aria-hidden className="flex-1" />
          <div className="w-full flex-shrink-0">
            <Footer />
          </div>
          <Card3DSForm />
          <ProcessingLoader />
          <ScreenDrawer />
          {(redirectUrl || showRedirect) && <RedirectPayment />}
          {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
        </div>
      </ExpressRevealContext.Provider>
    </Modal>
  );
};

interface PreferredPaymentDrawerViewProps {
  revealValue: ExpressRevealValue;
  onClose: () => void;
  useOtherPaymentMethodLink: ReactNode;
  showRedirect: boolean;
  showSuccessScreen: boolean;
}

/**
 * Bottom-sheet (Apple/Google-Pay-style) presentation of the PREFERRED view,
 * rendered only when the `useDrawer` gate in {@link PreferredPaymentView} is
 * satisfied (mobile portrait + flag on + supported primary method). Kept as its
 * own component so the drawer branch stays self-contained and the parent's
 * render path reads as a simple "sheet vs modal" fork.
 *
 * Layout intent (Figma): a PURE WHITE compact card — NO publisher hero inside
 * the sheet (the dimmed game is already visible behind it, so a second hero
 * band reads as busy). `Details` runs in its `bottomSheet` mode: forced dark
 * text (no hero to contrast against) and a totals-first breakdown — Subtotal /
 * Tax / Total, a hairline divider, then a background-less product list — with
 * no promo field or gray items box. The card HUGS its content (no min-height floor)
 * and is bottom-anchored, so it stays compact whether or not the offer shows a
 * Total row and the pay button lands in the one-handed thumb zone instead of
 * floating mid-sheet above a stretch of dead space. No footer / divider: the
 * swipe sheet is a minimal one-tap card; the full footer lives on the DEFAULT
 * flow behind "Use Other Payment Method".
 *
 * Scroll split: `Details` scrolls inside the sheet; the pay CTA + "Use other"
 * link are PINNED below it (`shrink-0`). Expanding the breakdown must never
 * push the escape hatch below the fold. Sheet-level bottom inset (see
 * `useCheckoutDrawerBottomInset`) clears host browser toolbars that overlay
 * the checkout iframe — scoped to this checkout swipe-up sheet only, not
 * `Common/Drawer` / Modal.
 *
 * Ungated by design (Sprint-90): the boot loader is a centered spinner shown
 * until `isCheckoutReady` — so by the time this sheet mounts the app is
 * already ready to render, and the wallet button appears inside the (already
 * opened) sheet as soon as it mounts. The Apple Pay probe still runs and
 * `PreferredPaymentTemplate.onExpressWalletUnavailable` still swaps to
 * DEFAULT if the wallet turns out unsupported. The full-screen modal branch
 * (see the parent) keeps its wallet-gate skeleton untouched.
 */
const PreferredPaymentDrawerView = ({
  revealValue,
  onClose,
  useOtherPaymentMethodLink,
  showRedirect,
  showSuccessScreen,
}: PreferredPaymentDrawerViewProps) => {
  const {
    boot: { couponsConfiguration },
  } = useContext(CheckoutContext);
  return (
    <ExpressRevealContext.Provider value={revealValue}>
      {/* Everything INSIDE the sheet reads `BottomSheetContext` to shed the
          DEFAULT/SINGLE chrome (Details' gray box + promo, the saved-card /
          saved-PayPal account "⋮" menu). Scoped to the sheet subtree only — the
          sibling full-screen overlays below keep their normal presentation. */}
      <BottomSheetContext.Provider value={true}>
        <CheckoutBottomSheet onClose={onClose}>
          {/* Always white — the swipe-up sheet never paints the publisher hero,
              so `Details` (in `bottomSheet` mode) keeps the offer name / Details /
              Total in black (the auto-contrast hook would pick white for the hero
              and go invisible here) and orders the breakdown totals-first.
              Flex column: Details may scroll; the CTA footer is pinned so the
              pay button + "Use other" stay visible. Details uses `min-h-0` +
              shrink (NOT `flex-1`) so a short offer still hugs content. */}
          <div data-testid="preferred-payment-method" className="flex min-h-0 w-full flex-col bg-white">
            {/* `pt-4` top-aligns the publisher logo row with the sheet's
                overlaid top-right X close button (both sit at a 16px inset).
                When the sheet hits max-height, this region shrinks and scrolls;
                the pinned CTA below stays visible. */}
            <div data-testid="preferred-payment-background" className="min-h-0 w-full overflow-y-auto px-4 pt-4 pb-0">
              <Details defaultOpen={false} />
            </div>
            {couponsConfiguration?.enabled && (
              <div className="pt-2">
                <PromoCode />
              </div>
            )}
            {/* Pinned CTA: never scrolls away. `pt-3` breathes under the offer
                header / Total; `gap-3` keeps "Use other" tight under the wallet
                button; `pb-10` cushions off the sheet's rounded bottom edge.
                Sheet-level bottom inset clears host browser toolbars. */}
            <div
              data-testid="preferred-payment-cta"
              className="flex w-full flex-shrink-0 flex-col gap-3 px-4 pt-3 pb-10"
            >
              <Payment />
              {useOtherPaymentMethodLink}
            </div>
          </div>
        </CheckoutBottomSheet>
      </BottomSheetContext.Provider>
      {/* Full-screen overlays render as SIBLINGS of the sheet so their own
          `fixed inset-0` covers the viewport and isn't clipped by the panel. */}
      <Card3DSForm />
      <ProcessingLoader />
      <ScreenDrawer />
      {showRedirect && <RedirectPayment />}
      {showSuccessScreen && <SuccessScreen />}
    </ExpressRevealContext.Provider>
  );
};
